// External Imports
import React, { useState } from 'react';
import PropTypes from 'prop-types';

import Chart from 'react-apexcharts';

import Accordion from 'react-bootstrap/Accordion';
import Alert from 'react-bootstrap/Alert';
import Card from 'react-bootstrap/Card';
import Col from 'react-bootstrap/Col';
import Row from 'react-bootstrap/Row';
import Spinner from 'react-bootstrap/Spinner';
import Tab from 'react-bootstrap/Tab';
import Tabs from 'react-bootstrap/Tabs';

import AccessDenied from './AccessDenied';

//Hooks
import { useAuth } from 'react-oidc-context';
import useToken from '../utils/useToken';
import useBearerToken from '../utils/useBearerToken';
import ForbiddenError from '../utils/error';
import { useTimezoneContext } from '../utils/TimezoneContext';
import { shiftToTimezone } from '../utils/tzFormat';

import { fetchSiteUtilisation } from '../utils/site';

export default function VolumeUtilisation({ site }) {
  const auth = useAuth();
  const { token, isValidToken } = useToken();
  const { isLoggedIn, getToken } = useBearerToken();
  const { timezone } = useTimezoneContext();

  // Local Data
  const [error, setError] = useState({ message: '', severity: 'primary', access_denied: false });
  const [loading, setLoading] = useState(true);

  const [utilisation_charts, setUtilisationCharts] = useState({});

  const capitalize = (str) => {
    return str.charAt(0).toUpperCase() + str.slice(1);
  };

  const timeframeLabels = {
    daily: { tab: 'Last 1 Day', chart: 'Last 1 Day' },
    weekly: { tab: 'Last 1 Week', chart: 'Last 1 Week' },
    monthly: { tab: 'Last 1 Month', chart: 'Last 1 Month' },
    yearly: { tab: 'Last 1 Year', chart: 'Last 1 Year' },
  };

  const getTimeframeLabel = (timeframe, key) => {
    return timeframeLabels[timeframe]?.[key] ?? capitalize(timeframe);
  };

  const formatGB = (b) => {
    if (b < 1024) {
      return Math.round(b) + ' B';
    }
    if (b < 1024 * 1024) {
      return Math.round(b / 1024) + ' KB';
    }
    if (b < 1024 * 1024 * 1024) {
      return Math.round(b / 1024 / 1024) + ' MB';
    }
    return Math.round((b / 1024 / 1024 / 1024) * 10) / 10 + ' GB';
  };

  const formatTimestamps = (timestamps) =>
    timestamps.map((ts) => shiftToTimezone(parseInt(ts * 1000), timezone));

  const createUtilisationChart = (timeframe, data) => {
    const isEmpty = Object.keys(data).length === 0 || Object.values(data).every((v) => v === 0);
    return {
      isEmpty,
      options: {
        chart: {
          id: timeframe,
          toolbar: {
            show: false,
          },
        },
        title: {
          text: getTimeframeLabel(timeframe, 'chart'),
        },
        labels: formatTimestamps(Object.keys(data)),
        xaxis: {
          type: 'datetime',
          labels: {
            datetimeUTC: true,
            datetimeFormatter: {
              year: 'yyyy',
              month: "MMM 'yy",
              day: 'dd MMM',
              hour: 'HH:mm',
            },
          },
          categories: formatTimestamps(Object.keys(data)),
        },
        tooltip: {
          x: {
            format: 'dd MMM HH:mm',
          },
        },
        dataLabels: {
          enabled: false,
        },
        yaxis: {
          labels: {
            formatter: formatGB,
          },
        },
      },
      series: [
        {
          name: 'utilisation',
          data: Object.values(data),
        },
      ],
    };
  };

  const getSiteUtilisation = async (customerId, siteId) => {
    if (!isLoggedIn(auth, isValidToken)) {
      window.location.reload(false);
    }

    const bearerToken = getToken(auth, isValidToken, token);
    if (bearerToken == '') {
      return;
    }

    setLoading(true);

    fetchSiteUtilisation(customerId, siteId, bearerToken)
      .then((data) => {
        const charts = Object.keys(data.utilisation).map(function (t) {
          return [t, createUtilisationChart(t, data.utilisation[t]['chargeable_bytes'])];
        });
        setUtilisationCharts(charts);

        setError({ message: '', severity: 'success', access_denied: false });
        setLoading(false);
      })
      .catch((error) => {
        if (error instanceof ForbiddenError) {
          setError({ message: '', severity: 'danger', access_denied: true });
        } else {
          setError({
            message: 'Could not retrieve site with volume data! ' + error,
            severity: 'danger',
            access_denied: false,
          });
        }
        setLoading(false);
      });
  };

  // Event Handlers
  const getUtilisation = (eventKey) => {
    if (eventKey == 'utilisation') {
      if (site) {
        getSiteUtilisation(site.customer_id, site.site_id);
      }
    }
  };

  return (
    <Row className='mb-3'>
      <Col>
        <Accordion as={Card} flush onSelect={(eventKey) => getUtilisation(eventKey)}>
          <Accordion.Item eventKey='utilisation'>
            <Accordion.Header>Historical Utilisation</Accordion.Header>
            <Accordion.Body>
              {error.message != '' ? (
                <Alert variant={error.severity}>
                  <span>{error.message}</span>
                </Alert>
              ) : (
                <>
                  {error.access_denied ? (
                    <AccessDenied />
                  ) : (
                    <>
                      {loading ? (
                        <Alert variant='warning'>
                          <Spinner animation='border' role='status'>
                            <span className='visually-hidden'>Loading...</span>
                          </Spinner>
                          <span>Data loading...</span>
                        </Alert>
                      ) : (
                        <>
                          {Object.keys(utilisation_charts).length == 0 ? (
                            <Alert variant='warning'>
                              <span>No historical data available!</span>
                            </Alert>
                          ) : (
                            <Tabs id='uncontrolled-tab-example' defaultActiveKey='daily'>
                              {utilisation_charts.map((entry) => (
                                <Tab
                                  key={entry[0]}
                                  eventKey={entry[0]}
                                  title={getTimeframeLabel(entry[0], 'tab')}
                                >
                                  {entry[1].isEmpty ? (
                                    <Alert variant='info' className='mt-3'>
                                      <span>No data consumed in this period.</span>
                                    </Alert>
                                  ) : (
                                    <Chart
                                      type='bar'
                                      options={entry[1].options}
                                      series={entry[1].series}
                                      height='250px'
                                    />
                                  )}
                                </Tab>
                              ))}
                            </Tabs>
                          )}
                        </>
                      )}
                    </>
                  )}
                </>
              )}
            </Accordion.Body>
          </Accordion.Item>
        </Accordion>
      </Col>
    </Row>
  );
}

VolumeUtilisation.propTypes = {
  site: PropTypes.object,
};
